<template>
  <v-container>
    <v-layout class="sale-info" row wrap>
      <v-flex xs12 sm4>
        <h3>Payments</h3>
      </v-flex>
      <v-flex xs12 sm8 class="payment--detail">
        <span class="payment-item payment-date">
          {{dotDate(receipt.paymentDate)}}
        </span>
        <span class="payment-item payment-amount">
          {{currency(receipt.totalPayment)}}
        </span>
      </v-flex>
    </v-layout>

    <!-- Full Refund Alert Box -->
    <v-layout v-if="hasRefund && !isPartiallyRefunded" row wrap>
      <v-flex xs12>
        <div class="refund-alert">
          <div class="refund-alert-icon">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <path
                d="M12 2L2 22h20L12 2zm0 4l7.5 14h-15L12 6z"
                fill="#d32f2f"/>
              <path
                d="M11 15v-4h2v4h-2zm0 2v2h2v-2h-2z"
                fill="#d32f2f"/>
            </svg>
          </div>
          <div class="refund-alert-text">
            This payment ({{currency(refundAmount)}}) was refunded on {{formattedRefundDate}}
          </div>
        </div>
      </v-flex>
    </v-layout>

    <!-- Partial Refund Alert Box -->
    <v-layout v-if="isPartiallyRefunded" row wrap>
      <v-flex xs12>
        <div class="refund-alert">
          <div class="refund-alert-icon">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <path
                d="M12 2L2 22h20L12 2zm0 4l7.5 14h-15L12 6z"
                fill="#d32f2f"/>
              <path
                d="M11 15v-4h2v4h-2zm0 2v2h2v-2h-2z"
                fill="#d32f2f"/>
            </svg>
          </div>
          <div class="refund-alert-text">
            <div>This payment has been partially refunded:</div>
            <div
              v-for="(refund, index) in refundHistoryItems"
              :key="index"
              class="refund-history-item">
              • {{currency(refund.amount)}} — {{formatRefundItemDate(refund.timestamp)}}
            </div>
            <div class="refund-total">
              <strong>Total Refunded:</strong>
              {{currency(totalAmountRefunded)}} of {{currency(receipt.totalPayment)}}
            </div>
            <div
              v-if="receipt.refundReason
                && receipt.showRefundToCustomer"
              class="refund-reason">
              <strong>Refund reason:</strong> {{receipt.refundReason}}
            </div>
            <div class="net-amount-paid">
              <strong>Net Amount Paid:</strong>
              <span class="net-amount-value">{{currency(netAmountPaid)}}</span>
            </div>
          </div>
        </div>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import { Mixins } from '@spectorasoftware/homegauge-vue';

const { CurrencyFilter, DateTime } = Mixins;

export default {
  mixins: [DateTime, CurrencyFilter],
  props: {
    receipt: {
      required: true,
      type: Object,
    },
  },
  computed: {
    hasRefund() {
      return this.receipt
        && this.receipt.amountRefunded
        && this.receipt.amountRefunded > 0;
    },
    isPartiallyRefunded() {
      return (this.receipt?.amountRefunded ?? 0) > 0
        && (this.receipt?.refundHistory ?? []).length > 0;
    },
    refundHistoryItems() {
      return this.receipt?.refundHistory ?? [];
    },
    totalAmountRefunded() {
      return this.receipt?.amountRefunded ?? 0;
    },
    netAmountPaid() {
      return (this.receipt?.totalPayment ?? 0) - (this.receipt?.amountRefunded ?? 0);
    },
    refundAmount() {
      return this.receipt?.amountRefunded || 0;
    },
    refundDate() {
      return this.receipt?.refundedDateTime || '';
    },
    formattedRefundDate() {
      if (!this.refundDate) return '';
      const date = new Date(this.refundDate);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    },
  },
  methods: {
    formatRefundItemDate(timestamp) {
      if (!timestamp) return '';
      const {
        year, monthValue, dayOfMonth, hour = 0, minute = 0,
      } = timestamp;
      const date = new Date(year, monthValue - 1, dayOfMonth, hour, minute);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
h3 {
  line-height: initial;
  text-transform: uppercase;
}
.payment--detail {
  text-align: right;
}
.payment-item {
  display: inline-block;
  padding: 0 10px;
  &:first-child {
    padding: 0 10px 0 0;
  }
  &:last-child {
    padding: 0 0 0 10px;
  }
}
.refund-alert {
  display: flex;
  align-items: flex-start;
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 16px 20px;
  margin-top: 20px;
  gap: 16px;
  .refund-alert-icon {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 2px;
    svg {
      width: 24px;
      height: 24px;
    }
  }
  .refund-alert-text {
    flex: 1;
    font-size: 16px;
    color: #333;
    line-height: 1.5;
  }
}
.refund-history-item {
  margin-top: 4px;
  font-size: 15px;
}
.refund-total {
  margin-top: 8px;
  font-size: 15px;
}
.refund-reason {
  margin-top: 4px;
  font-size: 15px;
}
.net-amount-paid {
  margin-top: 8px;
  font-size: 15px;
  padding-top: 8px;
  border-top: 1px solid #ddd;
  .net-amount-value {
    font-weight: 600;
    color: #e65100;
    padding-left: 6px;
  }
}
</style>
